import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Space,
  Typography,
  Card,
  Tag,
  Upload,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  fetchPartnerLogos,
  createPartnerLogo,
  updatePartnerLogo,
  deletePartnerLogo,
} from "../../../api/partnerLogosApi";
import { withBase } from "../../../config/env";

const { Title, Text } = Typography;

export default function PartnerLogosPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchPartnerLogos();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toastError("Failed to fetch logos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const showCreateModal = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, display_order: 0 });
    setModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      name: record.name,
      website_url: record.website_url || "",
      display_order: record.display_order || 0,
      is_active: Number(record.is_active) === 1,
      logo: record.logo
        ? {
            fileList: [
              {
                uid: "-1",
                name: "logo",
                status: "done",
                url: withBase(record.logo),
              },
            ],
          }
        : undefined,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const fileObj = values.logo?.fileList?.[0]?.originFileObj;
      if (!editing && !fileObj) {
        toastError("Please upload a logo");
        return;
      }

      const payload = {
        name: values.name.trim(),
        website_url: (values.website_url || "").trim(),
        display_order: values.display_order ?? 0,
        is_active: values.is_active ? 1 : 0,
      };
      if (fileObj) payload.logo = fileObj;

      setSaving(true);
      if (editing) {
        await updatePartnerLogo(editing.id, payload);
        toastSuccess("Logo updated");
      } else {
        await createPartnerLogo(payload);
        toastSuccess("Logo created");
      }
      setModalVisible(false);
      load();
    } catch (err) {
      if (!err.errorFields) toastError("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePartnerLogo(id);
      toastSuccess("Logo deleted");
      load();
    } catch {
      toastError("Delete failed");
    }
  };

  const columns = [
    {
      title: "Logo",
      dataIndex: "logo",
      width: 110,
      render: (val, record) =>
        val ? (
          <img
            src={withBase(val)}
            alt={record.name}
            style={{ width: 88, height: 44, objectFit: "contain", background: "#fff" }}
          />
        ) : (
          <Text type="secondary">No image</Text>
        ),
    },
    { title: "Company", dataIndex: "name", render: (text) => <Text strong>{text}</Text> },
    {
      title: "Website",
      dataIndex: "website_url",
      render: (url) =>
        url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Order",
      dataIndex: "display_order",
      sorter: (a, b) => (a.display_order || 0) - (b.display_order || 0),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (isActive) => {
        const active = Number(isActive) === 1;
        return (
          <Tag icon={active ? <CheckOutlined /> : <CloseOutlined />} color={active ? "success" : "error"}>
            {active ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            type="text"
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => showEditModal(record)}
          />
          <Popconfirm title="Delete this logo?" onConfirm={() => handleDelete(record.id)} okText="Delete">
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={4} style={{ margin: 0 }}>
              Partner Logos
            </Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
              New Logo
            </Button>
          </div>
        }
        bordered={false}
      >
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          bordered
        />
      </Card>

      <Modal
        title={editing ? "Edit Logo" : "New Logo"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Company name" rules={[{ required: true, min: 2 }]}>
            <Input placeholder="Company name" />
          </Form.Item>
          <Form.Item name="website_url" label="Website URL">
            <Input placeholder="https://example.com" />
          </Form.Item>
          <Form.Item name="display_order" label="Display order">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="logo" label="Logo">
            <Upload beforeUpload={() => false} maxCount={1} listType="picture-card" accept="image/*">
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
